import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Pencil, Trash2, Users, Upload, Camera, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamsPage() {
  const { token, organization } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({ name: '', bio: '', city: '', years_in_competition: '', best_record: '' });
  
  // Logo upload states
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (organization) {
      fetchTeams();
    }
  }, [organization]);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  // Logo handling functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image must be less than 2MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error('Only JPG, PNG, and WebP images are allowed');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCamera(true);
    } catch (error) {
      toast.error('Could not access camera. Please check permissions.');
      console.error('Camera error:', error);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'logo.jpg', { type: 'image/jpeg' });
        setLogoFile(file);
        setLogoPreview(canvas.toDataURL('image/jpeg'));
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadLogo = async (teamId) => {
    if (!logoFile) return;
    
    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', logoFile);
      
      const response = await fetch(`${API_URL}/api/teams/${teamId}/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (response.ok) {
        toast.success('Logo uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload logo');
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTeam) {
        const response = await fetch(`${API_URL}/api/teams/${editingTeam.team_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ name: formData.name, bio: formData.bio, city: formData.city, years_in_competition: formData.years_in_competition, best_record: formData.best_record })
        });
        if (!response.ok) {
          const errorText = await response.text();
          try {
            const error = JSON.parse(errorText);
            throw new Error(error.detail || 'Failed to update team');
          } catch {
            throw new Error('Failed to update team');
          }
        }
        
        // Upload logo if selected
        if (logoFile) {
          await uploadLogo(editingTeam.team_id);
        }
        toast.success('Team updated!');
      } else {
        const response = await fetch(`${API_URL}/api/teams`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: formData.name,
            bio: formData.bio,
            city: formData.city,
            years_in_competition: formData.years_in_competition,
            best_record: formData.best_record,
            org_id: organization.org_id
          })
        });
        
        const responseText = await response.text();
        let newTeam;
        try {
          newTeam = JSON.parse(responseText);
        } catch {
          throw new Error('Failed to create team');
        }
        
        if (!response.ok) {
          throw new Error(newTeam.detail || 'Failed to create team');
        }
        
        // Upload logo if selected
        if (logoFile) {
          await uploadLogo(newTeam.team_id);
        }
        toast.success('Team created!');
      }
      
      resetForm();
      fetchTeams();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingTeam(null);
    setFormData({ name: '' });
    setLogoFile(null);
    setLogoPreview(null);
    stopCamera();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (teamId) => {
    if (!confirm('Are you sure you want to delete this team?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/teams/${teamId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete team');
      toast.success('Team deleted!');
      fetchTeams();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({ name: team.name, bio: team.bio || '', city: team.city || '', years_in_competition: team.years_in_competition || '', best_record: team.best_record || '' });
    // Set existing logo preview if available
    if (team.logo) {
      setLogoPreview(team.logo);
    }
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-emerald-500">Loading teams...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold text-black uppercase">Teams</h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            if (!open) resetForm();
            setDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="add-team-btn">
                <Plus size={18} className="mr-2" />
                Add Team
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1b] border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-black font-heading uppercase">
                  {editingTeam ? 'Edit Team' : 'Add New Team'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Team Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter team name"
                    required
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="team-name-input"
                  />
                </div>
                
                {/* Logo Upload Section */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Team Logo (Optional)</Label>
                  
                  {/* Camera View */}
                  {showCamera && (
                    <div className="relative bg-black rounded-lg overflow-hidden">
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        className="w-full h-48 object-cover"
                      />
                      <canvas ref={canvasRef} className="hidden" />
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                        <Button
                          type="button"
                          onClick={capturePhoto}
                          className="bg-emerald-500 hover:bg-emerald-600"
                        >
                          <Camera size={16} className="mr-1" />
                          Capture
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={stopCamera}
                          className="text-black bg-black/50"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Logo Preview */}
                  {!showCamera && logoPreview && (
                    <div className="relative w-20 h-20 mx-auto">
                      <img 
                        src={logoPreview} 
                        alt="Preview" 
                        className="w-20 h-20 rounded-lg object-cover border-2 border-emerald-500/30"
                      />
                      <button
                        type="button"
                        onClick={clearLogo}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-black hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Buttons */}
                  {!showCamera && !logoPreview && (
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Upload size={16} className="mr-2" />
                        Upload Logo
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={startCamera}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Camera size={16} className="mr-2" />
                        Take Photo
                      </Button>
                    </div>
                  )}
                  
                  {!showCamera && logoPreview && !logoFile && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Upload size={14} className="mr-1" />
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startCamera}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Camera size={14} className="mr-1" />
                        Retake
                      </Button>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">Max 2MB. JPG, PNG, or WebP</p>
                </div>
                
                {/* Team Details */}
                <div className="space-y-2">
                  <Label className="text-gray-700">About the Team</Label>
                  <Input
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    placeholder="Brief description of the team"
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="team-bio-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="e.g., Edmonton, AB"
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="team-city-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Years in Competition</Label>
                    <Input
                      value={formData.years_in_competition}
                      onChange={(e) => setFormData({...formData, years_in_competition: e.target.value})}
                      placeholder="e.g., 5 years"
                      className="bg-gray-100 border-gray-200 text-black"
                      data-testid="team-years-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Best Record</Label>
                    <Input
                      value={formData.best_record}
                      onChange={(e) => setFormData({...formData, best_record: e.target.value})}
                      placeholder="e.g., Champions 2024"
                      className="bg-gray-100 border-gray-200 text-black"
                      data-testid="team-record-input"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={resetForm}
                    className="flex-1 text-gray-600"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                    disabled={uploadingLogo}
                    data-testid="save-team-btn"
                  >
                    {uploadingLogo ? 'Uploading...' : (editingTeam ? 'Update Team' : 'Create Team')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Teams List */}
        {teams.length === 0 ? (
          <Card className="bg-[#1a1a1b] border-gray-200">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-emerald-400" size={32} />
              </div>
              <h3 className="text-lg font-heading font-bold text-black uppercase mb-2">No Teams Yet</h3>
              <p className="text-gray-600 mb-4">Add your first team to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {teams.map((team) => (
              <Card key={team.team_id} className="bg-[#1a1a1b] border-gray-200" data-testid={`team-card-${team.team_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {team.logo ? (
                        <img 
                          src={team.logo} 
                          alt={team.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-emerald-400 text-lg font-bold">
                            {team.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="text-black font-heading font-bold uppercase text-sm">{team.name}</h3>
                        {team.pool && (
                          <span className="text-emerald-400 text-xs font-medium">Pool {team.pool}</span>
                        )}
                        {team.city && (
                          <p className="text-gray-600 text-xs">{team.city}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(team)}
                        className="text-gray-600 hover:text-black h-8 w-8 p-0"
                        data-testid={`edit-team-${team.team_id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(team.team_id)}
                        className="text-gray-600 hover:text-red-500 h-8 w-8 p-0"
                        data-testid={`delete-team-${team.team_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  {team.bio && (
                    <p className="text-gray-600 text-xs mb-2 line-clamp-2">{team.bio}</p>
                  )}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                    {team.years_in_competition && (
                      <span>Years: {team.years_in_competition}</span>
                    )}
                    {team.best_record && (
                      <span>Best: {team.best_record}</span>
                    )}
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
                    P{team.played || 0} W{team.won || 0} D{team.drawn || 0} L{team.lost || 0} • {team.points || 0} pts
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
